import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Newspaper, Calendar, UserCog, Megaphone, BarChart3, FolderOpen, MessageSquare, ClipboardList, Activity, Trash2, CheckCircle, XCircle, Plus, X } from "lucide-react";

const TABS = [
  { key: "members", icon: Users },
  { key: "news", icon: Newspaper },
  { key: "events", icon: Calendar },
  { key: "executive", icon: UserCog },
  { key: "announcements", icon: Megaphone },
  { key: "bargaining", icon: BarChart3 },
  { key: "resources", icon: FolderOpen },
  { key: "community", icon: Users },
  { key: "surveys", icon: ClipboardList },
  { key: "leadership", icon: Activity },
  { key: "submissions", icon: MessageSquare },
];

function Input({ label, ...p }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      <input {...p} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md outline-none focus:border-[#d81b7a] focus:ring-2 focus:ring-pink-100 text-sm" />
    </label>
  );
}
function TArea({ label, ...p }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      <textarea {...p} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md outline-none focus:border-[#d81b7a] focus:ring-2 focus:ring-pink-100 text-sm min-h-[80px]" />
    </label>
  );
}
function Section({ title, action, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-lg text-slate-900">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}
function Btn({ children, ...p }) {
  return <button {...p} className={`cupe-gradient text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-1 ${p.className || ""}`}>{children}</button>;
}

// --- Members ---
function MembersPanel() {
  const { t } = useLang();
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, kind) => {
    await api.post(`/admin/users/${id}/${kind}`);
    toast.success("Updated"); load();
  };
  const pending = users.filter((u) => u.status === "pending");
  const others = users.filter((u) => u.status !== "pending");
  return (
    <div className="space-y-5">
      <Section title={t("admin.pendingApprovals")}>
        {pending.length === 0 ? <p className="text-slate-400 text-sm">-</p> : (
          <div className="space-y-2">
            {pending.map((u) => (
              <div key={u.id} className="flex items-center justify-between bg-pink-50/40 border border-pink-100 rounded-md px-4 py-3">
                <div><div className="font-semibold text-slate-900">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></div>
                <div className="flex gap-2">
                  <button onClick={() => act(u.id, "approve")} data-testid={`approve-${u.id}`} className="bg-emerald-600 text-white px-3 py-1.5 rounded-md text-sm font-semibold inline-flex items-center gap-1"><CheckCircle size={14}/> {t("admin.approve")}</button>
                  <button onClick={() => act(u.id, "reject")} className="bg-slate-200 text-slate-700 px-3 py-1.5 rounded-md text-sm font-semibold inline-flex items-center gap-1"><XCircle size={14}/> {t("admin.reject")}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
      <Section title={t("admin.members")}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-500 text-xs uppercase"><th className="py-2">Name</th><th>Email</th><th>Role</th><th>Status</th></tr></thead>
            <tbody>
              {others.map((u) => (
                <tr key={u.id} className="border-t border-slate-100">
                  <td className="py-2 text-slate-900">{u.name}</td>
                  <td className="text-slate-600">{u.email}</td>
                  <td className="text-slate-600">{u.role}</td>
                  <td><span className={`px-2 py-0.5 rounded-full text-xs ${u.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{u.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

// --- News ---
function NewsPanel() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title_en: "", title_fr: "", content_en: "", content_fr: "", category: "Workplace", why_matters_en: "", why_matters_fr: "" });
  const load = () => api.get("/news").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); await api.post("/news", form); toast.success("Posted"); setForm({ ...form, title_en: "", title_fr: "", content_en: "", content_fr: "" }); load(); };
  const del = async (id) => { await api.delete(`/news/${id}`); load(); };
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <Section title={t("admin.newPost")}>
        <form onSubmit={submit} className="space-y-3">
          <Input label="Title EN" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} required />
          <Input label="Title FR" value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} required />
          <Input label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required />
          <TArea label="Content EN" value={form.content_en} onChange={(e) => setForm({ ...form, content_en: e.target.value })} required />
          <TArea label="Content FR" value={form.content_fr} onChange={(e) => setForm({ ...form, content_fr: e.target.value })} required />
          <Input label="Why matters EN" value={form.why_matters_en} onChange={(e) => setForm({ ...form, why_matters_en: e.target.value })} />
          <Input label="Why matters FR" value={form.why_matters_fr} onChange={(e) => setForm({ ...form, why_matters_fr: e.target.value })} />
          <Btn type="submit"><Plus size={14}/> {t("admin.create")}</Btn>
        </form>
      </Section>
      <Section title={t("admin.news")}>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {items.map((p) => (
            <div key={p.id} className="border border-slate-200 rounded-md p-3 flex justify-between gap-3">
              <div><div className="font-semibold text-sm text-slate-900">{p.title_en}</div><div className="text-xs text-slate-500">{p.category}</div></div>
              <button onClick={() => del(p.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={16}/></button>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// --- Events ---
function EventsPanel() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title_en: "", title_fr: "", description_en: "", description_fr: "", start_at: "", location: "", event_type: "membership_meeting" });
  const load = () => api.get("/events").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); const payload = { ...form, start_at: new Date(form.start_at).toISOString() }; await api.post("/events", payload); toast.success("Created"); setForm({ title_en: "", title_fr: "", description_en: "", description_fr: "", start_at: "", location: "", event_type: "membership_meeting" }); load(); };
  const del = async (id) => { await api.delete(`/events/${id}`); load(); };
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <Section title={t("admin.newEvent")}>
        <form onSubmit={submit} className="space-y-3">
          <Input label="Title EN" required value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} />
          <Input label="Title FR" required value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} />
          <Input label="Start date/time" type="datetime-local" required value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} />
          <Input label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          <Input label="Event type" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} />
          <TArea label="Description EN" value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} />
          <TArea label="Description FR" value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} />
          <Btn type="submit"><Plus size={14}/> {t("admin.create")}</Btn>
        </form>
      </Section>
      <Section title={t("admin.events")}>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {items.map((e) => (
            <div key={e.id} className="border border-slate-200 rounded-md p-3 flex justify-between gap-3">
              <div><div className="font-semibold text-sm text-slate-900">{e.title_en}</div><div className="text-xs text-slate-500">{new Date(e.start_at).toLocaleString()} - {e.location}</div></div>
              <button onClick={() => del(e.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={16}/></button>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// --- Executive ---
function ExecPanel() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const empty = { name: "", role_en: "", role_fr: "", photo_url: "", bio_en: "", bio_fr: "", email: "", office_hours: "", is_steward: false, order: 0 };
  const [form, setForm] = useState(empty);
  const load = () => api.get("/executives").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); await api.post("/executives", form); toast.success("Added"); setForm(empty); load(); };
  const del = async (id) => { await api.delete(`/executives/${id}`); load(); };
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <Section title={t("admin.newExec")}>
        <form onSubmit={submit} className="space-y-3">
          <Input label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Role EN" required value={form.role_en} onChange={(e) => setForm({ ...form, role_en: e.target.value })} />
          <Input label="Role FR" required value={form.role_fr} onChange={(e) => setForm({ ...form, role_fr: e.target.value })} />
          <Input label="Photo URL" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} />
          <Input label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input label="Office Hours" value={form.office_hours} onChange={(e) => setForm({ ...form, office_hours: e.target.value })} />
          <TArea label="Bio EN" value={form.bio_en} onChange={(e) => setForm({ ...form, bio_en: e.target.value })} />
          <TArea label="Bio FR" value={form.bio_fr} onChange={(e) => setForm({ ...form, bio_fr: e.target.value })} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_steward} onChange={(e) => setForm({ ...form, is_steward: e.target.checked })} /> Is shop steward</label>
          <Input label="Order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0") })} />
          <Btn type="submit"><Plus size={14}/> {t("admin.create")}</Btn>
        </form>
      </Section>
      <Section title={t("admin.executive")}>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {items.map((e) => (
            <div key={e.id} className="border border-slate-200 rounded-md p-3 flex justify-between gap-3">
              <div><div className="font-semibold text-sm text-slate-900">{e.name}</div><div className="text-xs text-slate-500">{e.role_en}{e.is_steward ? " (steward)" : ""}</div></div>
              <button onClick={() => del(e.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={16}/></button>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// --- Announcements ---
function AnnPanel() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ message_en: "", message_fr: "", level: "info", active: true });
  const load = () => api.get("/announcements").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); await api.post("/announcements", form); toast.success("Posted"); setForm({ ...form, message_en: "", message_fr: "" }); load(); };
  const del = async (id) => { await api.delete(`/announcements/${id}`); load(); };
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <Section title={t("admin.newAnnouncement")}>
        <form onSubmit={submit} className="space-y-3">
          <TArea label="Message EN" required value={form.message_en} onChange={(e) => setForm({ ...form, message_en: e.target.value })} />
          <TArea label="Message FR" required value={form.message_fr} onChange={(e) => setForm({ ...form, message_fr: e.target.value })} />
          <Btn type="submit"><Plus size={14}/> {t("admin.create")}</Btn>
        </form>
      </Section>
      <Section title={t("admin.announcements")}>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {items.map((a) => (
            <div key={a.id} className="border border-slate-200 rounded-md p-3 flex justify-between gap-3">
              <div className="text-sm text-slate-700">{a.message_en}</div>
              <button onClick={() => del(a.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={16}/></button>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// --- Bargaining ---
function BargPanel() {
  const { t } = useLang();
  const [form, setForm] = useState({ current_stage: 1, update_en: "", update_fr: "" });
  useEffect(() => { api.get("/bargaining-status").then((r) => setForm({ current_stage: r.data.current_stage, update_en: r.data.update_en, update_fr: r.data.update_fr })); }, []);
  const save = async (e) => { e.preventDefault(); await api.put("/bargaining-status", form); toast.success("Saved"); };
  return (
    <Section title={t("admin.bargaining")}>
      <form onSubmit={save} className="space-y-3 max-w-xl">
        <Input label="Current stage (1-5)" type="number" min={1} max={5} value={form.current_stage} onChange={(e) => setForm({ ...form, current_stage: parseInt(e.target.value || "1") })} />
        <TArea label="Update EN" value={form.update_en} onChange={(e) => setForm({ ...form, update_en: e.target.value })} />
        <TArea label="Update FR" value={form.update_fr} onChange={(e) => setForm({ ...form, update_fr: e.target.value })} />
        <Btn type="submit">Save</Btn>
      </form>
    </Section>
  );
}

// --- Resources ---
function ResPanel() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title_en: "", title_fr: "", category: "Bylaws", file_url: "", description_en: "", description_fr: "" });
  const load = () => api.get("/resources").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); await api.post("/resources", form); toast.success("Added"); setForm({ ...form, title_en: "", title_fr: "", file_url: "" }); load(); };
  const del = async (id) => { await api.delete(`/resources/${id}`); load(); };
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <Section title={t("admin.newResource")}>
        <form onSubmit={submit} className="space-y-3">
          <Input label="Title EN" required value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} />
          <Input label="Title FR" required value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} />
          <Input label="Category" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <Input label="File URL" required value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })} />
          <TArea label="Description EN" value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} />
          <TArea label="Description FR" value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} />
          <Btn type="submit"><Plus size={14}/> {t("admin.create")}</Btn>
        </form>
      </Section>
      <Section title={t("admin.resources")}>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {items.map((r) => (
            <div key={r.id} className="border border-slate-200 rounded-md p-3 flex justify-between gap-3">
              <div><div className="font-semibold text-sm">{r.title_en}</div><div className="text-xs text-slate-500">{r.category}</div></div>
              <button onClick={() => del(r.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={16}/></button>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// --- Community ---
function CommunityPanel() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ post_type: "spotlight", title_en: "", title_fr: "", content_en: "", content_fr: "", image_url: "" });
  const load = () => api.get("/community").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); await api.post("/community", form); toast.success("Added"); setForm({ ...form, title_en: "", title_fr: "", content_en: "", content_fr: "", image_url: "" }); load(); };
  const del = async (id) => { await api.delete(`/community/${id}`); load(); };
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <Section title={t("admin.newCommunity")}>
        <form onSubmit={submit} className="space-y-3">
          <label className="block text-xs font-semibold text-slate-600">Post type
            <select className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" value={form.post_type} onChange={(e) => setForm({ ...form, post_type: e.target.value })}>
              {["spotlight", "win", "milestone", "birthday", "retirement", "new_hire", "wellness"].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <Input label="Title EN" required value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} />
          <Input label="Title FR" required value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} />
          <Input label="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
          <TArea label="Content EN" value={form.content_en} onChange={(e) => setForm({ ...form, content_en: e.target.value })} />
          <TArea label="Content FR" value={form.content_fr} onChange={(e) => setForm({ ...form, content_fr: e.target.value })} />
          <Btn type="submit"><Plus size={14}/> {t("admin.create")}</Btn>
        </form>
      </Section>
      <Section title={t("admin.community")}>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {items.map((c) => (
            <div key={c.id} className="border border-slate-200 rounded-md p-3 flex justify-between gap-3">
              <div><div className="font-semibold text-sm">{c.title_en}</div><div className="text-xs text-slate-500">{c.post_type}</div></div>
              <button onClick={() => del(c.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={16}/></button>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// --- Surveys ---
function SurveysPanel() {
  const { t } = useLang();
  const [form, setForm] = useState({ question_en: "", question_fr: "", options_en: "Yes\nNo", options_fr: "Oui\nNon" });
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/surveys", {
      question_en: form.question_en, question_fr: form.question_fr,
      options_en: form.options_en.split("\n").filter(Boolean),
      options_fr: form.options_fr.split("\n").filter(Boolean),
    });
    toast.success("Survey created");
    setForm({ question_en: "", question_fr: "", options_en: "Yes\nNo", options_fr: "Oui\nNon" });
  };
  return (
    <Section title={t("admin.newSurvey")}>
      <form onSubmit={submit} className="space-y-3 max-w-2xl">
        <Input label="Question EN" required value={form.question_en} onChange={(e) => setForm({ ...form, question_en: e.target.value })} />
        <Input label="Question FR" required value={form.question_fr} onChange={(e) => setForm({ ...form, question_fr: e.target.value })} />
        <TArea label="Options EN (one per line)" value={form.options_en} onChange={(e) => setForm({ ...form, options_en: e.target.value })} />
        <TArea label="Options FR (one per line)" value={form.options_fr} onChange={(e) => setForm({ ...form, options_fr: e.target.value })} />
        <Btn type="submit"><Plus size={14}/> {t("admin.create")}</Btn>
      </form>
    </Section>
  );
}

// --- Leadership ---
function LeadershipPanel() {
  const { t } = useLang();
  const [form, setForm] = useState({ action_type: "win_achieved", description_en: "", description_fr: "" });
  const submit = async (e) => { e.preventDefault(); await api.post("/leadership-actions", form); toast.success("Added"); setForm({ ...form, description_en: "", description_fr: "" }); };
  return (
    <Section title={t("admin.newAction")}>
      <form onSubmit={submit} className="space-y-3 max-w-2xl">
        <label className="block text-xs font-semibold text-slate-600">Type
          <select className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" value={form.action_type} onChange={(e) => setForm({ ...form, action_type: e.target.value })}>
            {["grievance_filed", "meeting_held", "safety_escalation", "win_achieved", "bargaining_update"].map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <TArea label="Description EN" required value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} />
        <TArea label="Description FR" required value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} />
        <Btn type="submit"><Plus size={14}/> {t("admin.create")}</Btn>
      </form>
    </Section>
  );
}

// --- Submissions ---
function SubsPanel() {
  const { t } = useLang();
  const [griev, setGriev] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [anon, setAnon] = useState([]);
  const [news, setNews] = useState([]);
  const [mc, setMc] = useState([]);
  useEffect(() => {
    api.get("/grievances").then((r) => setGriev(r.data)).catch(() => {});
    api.get("/contact").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/anonymous-reports").then((r) => setAnon(r.data)).catch(() => {});
    api.get("/newsletter").then((r) => setNews(r.data)).catch(() => {});
    api.get("/member-corner").then((r) => setMc(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-5">
      <Section title={t("admin.grievanceSubs")}>
        <div className="space-y-2">
          {griev.map((g) => (
            <div key={g.id} className="border border-slate-200 rounded-md p-3">
              <div className="text-sm font-semibold text-slate-900">{g.member_name} - {g.issue_type}</div>
              <div className="text-xs text-slate-500">{g.member_email} • {new Date(g.created_at).toLocaleString()}</div>
              <p className="text-sm text-slate-700 mt-1">{g.description}</p>
            </div>
          ))}
          {griev.length === 0 && <p className="text-slate-400 text-sm">-</p>}
        </div>
      </Section>
      <Section title={t("admin.contactSubs")}>
        <div className="space-y-2">
          {contacts.map((c) => (
            <div key={c.id} className="border border-slate-200 rounded-md p-3">
              <div className="text-sm font-semibold">{c.name} - {c.subject}</div>
              <div className="text-xs text-slate-500">{c.email} • {new Date(c.created_at).toLocaleString()}</div>
              <p className="text-sm text-slate-700 mt-1">{c.message}</p>
            </div>
          ))}
          {contacts.length === 0 && <p className="text-slate-400 text-sm">-</p>}
        </div>
      </Section>
      <Section title={t("admin.anonReports")}>
        <div className="space-y-2">
          {anon.map((a) => (
            <div key={a.id} className="border border-slate-200 rounded-md p-3">
              <div className="text-sm font-semibold">{a.category}</div>
              <div className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</div>
              <p className="text-sm text-slate-700 mt-1">{a.description}</p>
            </div>
          ))}
          {anon.length === 0 && <p className="text-slate-400 text-sm">-</p>}
        </div>
      </Section>
      <Section title={t("admin.newsletterSubs")}>
        <div className="space-y-2">
          {news.map((n) => (
            <div key={n.id} className="border border-slate-200 rounded-md px-3 py-2 flex justify-between items-center text-sm">
              <span className="text-slate-900">{n.email}</span>
              <span className="text-xs text-slate-400">{new Date(n.created_at).toLocaleDateString()}</span>
            </div>
          ))}
          {news.length === 0 && <p className="text-slate-400 text-sm">-</p>}
        </div>
      </Section>
      <Section title={t("admin.memberCornerSubs")}>
        <div className="space-y-2">
          {mc.map((m) => (
            <div key={m.id} className="border border-slate-200 rounded-md p-3">
              <div className="text-sm font-semibold text-slate-900">{m.name} - {m.category}</div>
              <div className="text-xs text-slate-500">{m.email} • {new Date(m.created_at).toLocaleString()} • Consent: {m.consent ? "Yes" : "No"}</div>
              <p className="text-sm text-slate-700 mt-1">{m.message}</p>
              {m.photo_url && <img src={m.photo_url} alt="" className="mt-2 h-24 rounded border border-slate-200" />}
            </div>
          ))}
          {mc.length === 0 && <p className="text-slate-400 text-sm">-</p>}
        </div>
      </Section>
    </div>
  );
}

const PANELS = {
  members: MembersPanel, news: NewsPanel, events: EventsPanel, executive: ExecPanel,
  announcements: AnnPanel, bargaining: BargPanel, resources: ResPanel, community: CommunityPanel,
  surveys: SurveysPanel, leadership: LeadershipPanel, submissions: SubsPanel,
};

export default function AdminDashboard() {
  const { t } = useLang();
  const { user, ready } = useAuth();
  const [tab, setTab] = useState("members");

  if (!ready) return <div className="p-12 text-center text-slate-500">{t("common.loading")}</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/portal" replace />;

  const Panel = PANELS[tab];

  return (
    <div data-testid="admin-page">
      <section className="cupe-gradient text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h1 className="font-display text-3xl font-bold">{t("admin.title")}</h1>
          <p className="opacity-90 text-sm mt-1">{user.email}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        <aside className="bg-white border border-slate-200 rounded-2xl p-3 h-fit sticky top-24 overflow-x-auto">
          <ul className="flex lg:flex-col gap-1 min-w-max lg:min-w-0">
            {TABS.map((tb) => (
              <li key={tb.key}>
                <button
                  onClick={() => setTab(tb.key)}
                  data-testid={`admin-tab-${tb.key}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-left whitespace-nowrap ${tab === tb.key ? "cupe-gradient text-white" : "text-slate-700 hover:bg-slate-100"}`}
                >
                  <tb.icon size={16}/> {t(`admin.${tb.key}`)}
                </button>
              </li>
            ))}
          </ul>
        </aside>
        <div>
          <Panel />
        </div>
      </section>
    </div>
  );
}
